import type { SiteEntity } from '../../types/site';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import type { ProjectIntent, ProjectPhase } from '../../types/siteRag';
import {
  ECONOMIC_CONTRIBUTION_SCHEMA_VERSION,
  type DataCenterOperatingModel,
  type DataCenterWorkload,
  type EconomicContributionScreenRequest,
  type EconomicContributionScenarioInput,
  type EconomicScreeningScenarioOption,
  type EconomicScreeningSiteDefaults,
  type PowerMwBasis,
  type ScreeningInputSource,
  type WaterUseAmbientConditions,
  type WaterUseInputContext,
} from '../../types/economicContribution';
import {
  effectivePhaseCapacity,
  effectivePower,
  effectivePowerMwRange,
} from '../projectIntent';
import { effectiveWorkloadProfile, type WorkloadProfile } from '../workloadProfile';
import { resolveIntentPowerStack, resolvePhasePowerStack } from '../powerStack';
import {
  demandUtilizationFromContextClaims,
  isAnnualDemandUtilizationRangePct,
} from '../intent/annualDemandUtilization';
import { isIntentFieldExplicitlyUnknown } from '../intent/explicitUnknown';
import type { ExtractedClaim } from '../../types/siteContext';
import { WATER_USE_BENCHMARK_CATALOG } from '../../data/economicContribution/waterUseBenchmarks';

function finitePositive(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

function projectPowerBasis(site: SiteEntity): {
  basis: PowerMwBasis;
  source: ScreeningInputSource;
} {
  if (isIntentFieldExplicitlyUnknown(site.projectIntent, 'powerBasis')) return { basis: 'facility', source: 'screening-assumption' };
  if (site.projectIntent?.powerBasis === 'it-load') return { basis: 'it', source: 'site-record' };
  if (site.projectIntent?.powerBasis === 'facility') return { basis: 'facility', source: 'site-record' };
  return { basis: 'facility', source: 'screening-assumption' };
}

function recordedWorkloadProfile(intent: ProjectIntent | undefined): WorkloadProfile | undefined {
  if (isIntentFieldExplicitlyUnknown(intent, 'workloadProfile')) return undefined;
  if (intent?.workloadProfile) return intent.workloadProfile;
  if (intent?.projectType && intent.projectType !== 'undecided') {
    const resolved = effectiveWorkloadProfile(intent);
    return resolved === 'unknown' ? undefined : resolved;
  }
  return undefined;
}

/** Reuse only Intent classifications that have a direct economic-model equivalent. */
export function projectEconomicWorkload(intent: ProjectIntent | undefined): DataCenterWorkload {
  switch (recordedWorkloadProfile(intent)) {
    case 'ai-inference':
      return 'inference';
    case 'ai-training':
    case 'hpc':
      return 'ai-hpc';
    case 'mixed':
      return 'mixed';
    default:
      return 'unknown';
  }
}

/** Infer an operating model only where the Intent taxonomy says the same thing. */
export function projectEconomicOperatingModel(intent: ProjectIntent | undefined): DataCenterOperatingModel {
  const profile = recordedWorkloadProfile(intent);
  if (profile === 'colocation') return 'colocation';
  if (profile === 'enterprise') return 'enterprise';
  if (intent?.siteSponsor?.type === 'hyperscaler') return 'hyperscale';
  return 'unknown';
}

function recordedPue(
  intent: ProjectIntent | undefined,
  phase?: ProjectPhase,
): WaterUseInputContext['pue'] | undefined {
  if (phase?.adoptSimulatedPue && finitePositive(phase.simulatedPueValue) && phase.simulatedPueValue >= 1) {
    return {
      low: phase.simulatedPueValue,
      high: phase.simulatedPueValue,
      source: 'site-record',
      sourceLabel: `${phase.label || 'Selected phase'} adopted simulation PUE`,
    };
  }
  const pueUnknown = isIntentFieldExplicitlyUnknown(intent, 'pueValue');
  const explicit = pueUnknown ? undefined : intent?.pueValue;
  const target = pueUnknown ? undefined : intent?.pueTarget;
  const value = finitePositive(explicit) && explicit >= 1
    ? { value: explicit, label: 'Site-record PUE' }
    : finitePositive(target) && target >= 1
      ? { value: target, label: 'Site-record PUE target' }
      : null;
  return value ? {
    low: value.value,
    high: value.value,
    source: 'site-record',
    sourceLabel: value.label,
  } : (() => {
    const heatRejection = phase?.bopHeatRejection ?? intent?.bopHeatRejection;
    const coolingGeneration = phase?.coolingGeneration ?? intent?.coolingGeneration;
    const hasRecordedCoolingConfiguration = Boolean(
      (intent?.coolingStrategy && intent.coolingStrategy !== 'undecided')
      || (heatRejection && heatRejection !== 'unknown')
      || (coolingGeneration && coolingGeneration !== 'unknown'),
    );
    if (!hasRecordedCoolingConfiguration) return undefined;
    const stack = phase
      ? resolvePhasePowerStack(phase, intent)
      : resolveIntentPowerStack(intent);
    if (!finitePositive(stack.pue) || stack.pue < 1) return undefined;
    const published = WATER_USE_BENCHMARK_CATALOG.defaults.pue;
    return {
      // A configuration estimate is useful context, but it is not measured or
      // simulated evidence. Keep it inside the published early-stage envelope
      // instead of collapsing every downstream water result to one heuristic.
      low: Math.min(published.low, stack.pue),
      high: Math.max(published.high, stack.pue),
      source: 'screening-assumption',
      sourceLabel: `Published early-stage PUE range with a ${stack.pue.toFixed(2)} configuration-derived estimate (${stack.pueSource.replaceAll('-', ' ')})`,
    };
  })();
}

function unanimousKnownValue<T>(
  values: readonly (T | null | undefined)[],
  unknownValues: ReadonlySet<T>,
): T | undefined {
  if (values.length === 0 || values.some(value => value == null || unknownValues.has(value))) return undefined;
  const first = values[0] as T;
  return values.every(value => value === first) ? first : undefined;
}

/**
 * Migration boundary for older site records that stored a cooling selection
 * only on every phase. A unanimous phase value can safely restore the
 * whole-project screen; mixed or incomplete phases remain unknown.
 */
function intentWithUnanimousLegacyCooling(intent: ProjectIntent | undefined): ProjectIntent | undefined {
  if (!intent || !intent.phases?.length) return intent;
  const rackCooling = intent.coolingStrategy && intent.coolingStrategy !== 'undecided'
    ? intent.coolingStrategy
    : unanimousKnownValue(
        intent.phases.map(phase => phase.coolingStrategy),
        new Set<ProjectIntent['coolingStrategy']>(['undecided']),
      );
  const heatRejection = intent.bopHeatRejection && intent.bopHeatRejection !== 'unknown'
    ? intent.bopHeatRejection
    : unanimousKnownValue(
        intent.phases.map(phase => phase.bopHeatRejection),
        new Set<NonNullable<ProjectIntent['bopHeatRejection']>>(['unknown']),
      );
  const coolingGeneration = intent.coolingGeneration && intent.coolingGeneration !== 'unknown'
    ? intent.coolingGeneration
    : unanimousKnownValue(
        intent.phases.map(phase => phase.coolingGeneration),
        new Set<NonNullable<ProjectIntent['coolingGeneration']>>(['unknown']),
      );
  if (
    rackCooling === intent.coolingStrategy
    && heatRejection === intent.bopHeatRejection
    && coolingGeneration === intent.coolingGeneration
  ) return intent;
  return {
    ...intent,
    ...(rackCooling ? { coolingStrategy: rackCooling } : {}),
    ...(heatRejection ? { bopHeatRejection: heatRejection } : {}),
    ...(coolingGeneration ? { coolingGeneration } : {}),
  };
}

/**
 * Project only the dedicated DATA-CENTER demand range. Generation capacity
 * factors intentionally never enter this resolver.
 */
function recordedDemandUtilization(
  intent: ProjectIntent | undefined,
  contextClaims?: readonly ExtractedClaim[] | null,
): WaterUseInputContext['utilization'] | undefined {
  // The epistemic marker wins over a stale/coexisting range. Normal UI writes
  // clear the range when Unknown is chosen, but imported or merged records can
  // carry both; never turn that conflict into a supposedly known fact.
  if (isIntentFieldExplicitlyUnknown(intent, 'annualDemandUtilizationRangePct')) {
    return undefined;
  }
  const range = intent?.annualDemandUtilizationRangePct;
  if (isAnnualDemandUtilizationRangePct(range)) {
    return {
      low: range.low / 100,
      high: range.high / 100,
      source: 'site-record',
      sourceLabel: 'Project Intent annual data-center demand utilization / load-factor range',
    };
  }
  const contextResolution = demandUtilizationFromContextClaims(contextClaims);
  return contextResolution ? {
    low: contextResolution.range.low / 100,
    high: contextResolution.range.high / 100,
    source: 'site-record',
    sourceLabel: contextResolution.sourceLabel,
  } : undefined;
}

const REPORT_DERIVED_CLIMATE_PROVENANCE = new Set([
  'report-derived',
  'report+provider',
  'report-overrides-provider',
]);

function finiteClimateValue(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/**
 * Project trusted local conditions only from the exact SED the site currently
 * owns. Report-derived, inferred, anomalous, carried-forward, and in-flight
 * climate is deliberately excluded from a quantitative screening request.
 */
export function waterUseAmbientConditionsFromSed(
  site: SiteEntity,
  sed: SiteEvidenceDocument | null | undefined,
): WaterUseAmbientConditions | undefined {
  if (!sed || !site.sedId || site.sedVersion == null) return undefined;
  if (site.sedStatus === 'bootstrapping' || site.sedStatus === 'failed') return undefined;
  if (sed.id !== site.sedId || sed.version !== site.sedVersion) return undefined;
  return waterUseAmbientConditionsFromSedClimate(sed);
}

/** Trusted frozen climate; caller establishes its own immutable/current SED binding. */
export function waterUseAmbientConditionsFromSedClimate(
  sed: SiteEvidenceDocument,
): WaterUseAmbientConditions | undefined {
  const section = sed.climate;
  if (!section?.data) return undefined;
  if (section.status === 'unavailable' || section.status === 'anomalous' || section.status === 'verified-from-report') return undefined;
  if ((section.anomalies?.length ?? 0) > 0 || section.carriedForward != null) return undefined;
  if (section.sourceGrade === 'inference') return undefined;
  if (section.qualification === 'unknown' || section.qualification === 'unavailable') return undefined;
  if (section.provenance && REPORT_DERIVED_CLIMATE_PROVENANCE.has(section.provenance)) return undefined;
  if (section.provenanceDetail?.derivedFromReport != null) return undefined;

  const climate = section.data;
  // Owner decision (28 Sep 2026): prefer the SED highest-monthly wet bulb,
  // which uses Open-Meteo's reanalysis monthly humidity, over the NOAA normals
  // estimate at an assumed RH (see noaaNormalsBasis.ts). NOAA is the fallback
  // only. Neither is a design percentile.
  const useNoaaWetBulb = !finiteClimateValue(climate.peakWetBulb)
    && finiteClimateValue(climate.noaaNormals?.peakDesignWetBulb);
  const designWetBulb = useNoaaWetBulb
    ? climate.noaaNormals!.peakDesignWetBulb!
    : climate.peakWetBulb;
  const values = [
    climate.avgTemp,
    climate.peakTemp,
    climate.avgWetBulb,
    designWetBulb,
    climate.humidity,
  ];
  if (!values.every(finiteClimateValue)) return undefined;
  if (
    climate.avgTemp < -90 || climate.avgTemp > 70
    || climate.peakTemp < climate.avgTemp || climate.peakTemp > 70
    || climate.avgWetBulb < -90 || climate.avgWetBulb > climate.avgTemp
    || designWetBulb < climate.avgWetBulb || designWetBulb > climate.peakTemp
    || climate.humidity < 0 || climate.humidity > 100
  ) return undefined;

  const provider = section.source?.trim() || 'site climate provider';
  // The NOAA station labels only a NOAA-derived wet bulb.
  const station = useNoaaWetBulb ? climate.noaaNormals?.stationName?.trim() : undefined;
  return {
    averageDryBulbC: climate.avgTemp,
    peakDryBulbC: climate.peakTemp,
    averageWetBulbC: climate.avgWetBulb,
    designWetBulbC: designWetBulb,
    wetBulbBasis: useNoaaWetBulb ? 'noaa-design' : 'sed-peak-proxy',
    relativeHumidityPct: climate.humidity,
    monthlyClimatePoints: Math.min(12, climate.monthlySummary?.length ?? 0),
    source: 'site-record',
    sourceLabel: `${provider} · current SED v${sed.version}`,
    sedId: sed.id,
    sedVersion: sed.version,
    ...(section.dataAsOf || section.retrievedAt ? { dataAsOf: section.dataAsOf ?? section.retrievedAt } : {}),
    ...(station ? { weatherStationLabel: station } : {}),
  };
}

function waterDefaults(
  intent: ProjectIntent | undefined,
  workload: DataCenterWorkload,
  operatingModel: DataCenterOperatingModel,
  phase?: ProjectPhase,
  contextClaims?: readonly ExtractedClaim[] | null,
): WaterUseInputContext {
  const pue = recordedPue(intent, phase);
  const utilization = recordedDemandUtilization(intent, contextClaims);
  const heatRejection = phase?.bopHeatRejection ?? intent?.bopHeatRejection;
  return {
    ...(pue ? { pue } : {}),
    ...(utilization ? { utilization } : {}),
    ...(heatRejection ? { heatRejection } : {}),
    ...(intent?.coolingStrategy ? { rackCoolingStrategy: intent.coolingStrategy } : {}),
    ...(workload !== 'unknown' ? { workload } : {}),
    ...(operatingModel !== 'unknown' ? { operatingModel } : {}),
  };
}

function constructionDuration(site: SiteEntity, phaseId?: string): number | undefined {
  const financials = site.financialAssumptions;
  if (phaseId) {
    const phaseDuration = financials?.phases?.find(phase => phase.phaseId === phaseId)?.constructionMonths;
    if (finitePositive(phaseDuration)) return phaseDuration;
  }
  return finitePositive(financials?.constructionMonths) ? financials.constructionMonths : undefined;
}

function closedRange(range: { min: number; max: number } | null): { low: number; high: number } | undefined {
  return range
    && finitePositive(range.min)
    && finitePositive(range.max)
    && range.max >= range.min
      ? { low: range.min, high: range.max }
      : undefined;
}

function scenarioLabel(label: string, loadMw?: number, range?: { low: number; high: number }): string {
  if (finitePositive(loadMw)) return `${label} — ${loadMw.toLocaleString('en-US')} MW cumulative`;
  if (range) return `${label} — ${range.low.toLocaleString('en-US')}–${range.high.toLocaleString('en-US')} MW cumulative`;
  return `${label} — MW not yet bounded`;
}

/**
 * Resolve source-aware form defaults from canonical site records. These values
 * are display defaults, not economic refinements, so an untouched form can
 * continue following later Intent edits instead of freezing a duplicate copy.
 */
export function resolveEconomicScreeningSiteDefaults(site: SiteEntity): EconomicScreeningSiteDefaults {
  const intent = intentWithUnanimousLegacyCooling(site.projectIntent);
  const power = effectivePower(intent);
  const loadRange = power.isExact ? undefined : closedRange(effectivePowerMwRange(power));
  const workload = projectEconomicWorkload(intent);
  const operatingModel = projectEconomicOperatingModel(intent);
  const loadBasisResolution = projectPowerBasis(site);
  const loadBasis = loadBasisResolution.basis;
  const contextClaims = site.siteContext?.claims;
  const wholeWater = waterDefaults(intent, workload, operatingModel, undefined, contextClaims);
  const wholeDuration = constructionDuration(site);
  const whole: EconomicScreeningScenarioOption = {
    id: 'declared-project',
    label: scenarioLabel('Whole project', power.mw ?? undefined, loadRange),
    phaseBasis: 'declared-project',
    ...(power.mw != null ? { loadMw: power.mw } : {}),
    ...(loadRange ? { loadRange } : {}),
    loadBasis,
    loadBasisSource: loadBasisResolution.source,
    operatingModel,
    workload,
    ...(wholeDuration ? { constructionDurationMonths: wholeDuration } : {}),
    waterUse: wholeWater,
    ...(!power.mw && !loadRange ? { disabledReason: 'Record an exact or bounded project MW value.' } : {}),
  };
  const phaseOptions = (intent?.phases ?? [])
    .slice()
    .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0))
    .filter(phase => typeof phase.id === 'string' && phase.id.trim().length > 0)
    .map((phase): EconomicScreeningScenarioOption => {
      const capacity = effectivePhaseCapacity(phase);
      const range = capacity.isExact ? undefined : closedRange(capacity.range);
      const phaseWorkload = projectEconomicWorkload({
        ...intent,
        ...(phase.workloadProfile ? { workloadProfile: phase.workloadProfile } : {}),
      } as ProjectIntent);
      const duration = constructionDuration(site, phase.id);
      return {
        id: phase.id,
        label: scenarioLabel(phase.label?.trim() || `Phase ${phase.sequence || ''}`.trim(), capacity.mw ?? undefined, range),
        phaseBasis: phase.id,
        ...(capacity.mw != null ? { loadMw: capacity.mw } : {}),
        ...(range ? { loadRange: range } : {}),
        loadBasis,
        loadBasisSource: loadBasisResolution.source,
        operatingModel,
        workload: phaseWorkload,
        ...(duration ? { constructionDurationMonths: duration } : {}),
        waterUse: waterDefaults(intent, phaseWorkload, operatingModel, phase, contextClaims),
        ...(!capacity.mw && !range ? { disabledReason: 'This phase has no closed MW value or range.' } : {}),
      };
    });
  const profile = site.economicProfileSnapshot;
  const jurisdictionLabel = profile?.countyName?.trim()
    ? `${profile.countyName.trim()}${profile.stateCode ? `, ${profile.stateCode}` : ''}`
    : undefined;
  return {
    ...(whole.loadMw != null ? { loadMw: whole.loadMw } : {}),
    ...(whole.loadRange ? { loadRange: whole.loadRange } : {}),
    loadBasis,
    loadBasisSource: loadBasisResolution.source,
    phaseBasis: whole.phaseBasis,
    operatingModel,
    workload,
    ...(whole.constructionDurationMonths ? { constructionDurationMonths: whole.constructionDurationMonths } : {}),
    waterUse: wholeWater,
    ...(jurisdictionLabel ? { jurisdictionLabel } : {}),
    scenarioOptions: [whole, ...phaseOptions],
  };
}

function selectedScenario(
  defaults: EconomicScreeningSiteDefaults,
  phaseBasis: string | undefined,
): EconomicScreeningScenarioOption {
  return defaults.scenarioOptions.find(option => option.phaseBasis === phaseBasis)
    ?? defaults.scenarioOptions[0]!;
}

/** Composes canonical site defaults first, then applies only explicit economic refinements. */
export function economicScreenRequestFromSite(
  site: SiteEntity,
  options: { sed?: SiteEvidenceDocument | null; scenario?: EconomicScreeningScenarioOption; includeScenarios?: boolean } = {},
): EconomicContributionScreenRequest | null {
  // A project-wide load/operator headcount must never be copied onto a phase.
  const refinements = options.scenario ? undefined : site.economicContributionInputs;
  const defaults = resolveEconomicScreeningSiteDefaults(site);
  const scenario = options.scenario ?? selectedScenario(defaults, refinements?.phaseBasis?.trim() || defaults.phaseBasis);
  const phaseBasis = options.scenario?.phaseBasis ?? (refinements?.phaseBasis?.trim() || scenario.phaseBasis);
  const explicitLoadMw = finitePositive(refinements?.loadMw) ? refinements.loadMw : undefined;
  const explicitRange = refinements?.loadRange;
  const validExplicitRange = explicitLoadMw == null
    && explicitRange != null
    && finitePositive(explicitRange.low)
    && finitePositive(explicitRange.high)
    && explicitRange.high >= explicitRange.low
      ? explicitRange
      : undefined;
  const loadMw = explicitLoadMw ?? (validExplicitRange ? undefined : scenario.loadMw);
  const loadRange = loadMw == null ? (validExplicitRange ?? scenario.loadRange) : undefined;
  if (!finitePositive(loadMw) && !loadRange) return null;

  const sourceBasis = refinements?.loadBasis ?? scenario.loadBasis;
  const basisSource: ScreeningInputSource = refinements?.loadBasis
    ? 'site-record'
    : scenario.loadBasisSource ?? 'site-record';
  // Unknown is preserved in Intent/SED. The deterministic model can still run
  // conditionally, with its assumed load boundary carried on every result.
  const hasWorkloadRefinement = refinements?.workload != null;
  const hasOperatingModelRefinement = refinements?.operatingModel != null;
  // Economic refinements use their enum sentinel as the persisted epistemic
  // state. `unknown` therefore means "the user explicitly kept this open",
  // not "fall back to an Intent-derived value". Only absence follows Intent.
  const workload = refinements?.workload ?? scenario.workload;
  const operatingModel = refinements?.operatingModel ?? scenario.operatingModel;
  const refinedWaterUse: WaterUseInputContext = { ...(refinements?.waterUse ?? {}) };
  // Ambient conditions are always re-projected from the exact current SED;
  // never let a copied/stale refinement override the current site record.
  delete refinedWaterUse.ambientConditions;
  const ambientConditions = waterUseAmbientConditionsFromSed(site, options.sed);
  const waterContext: WaterUseInputContext = {
    ...(scenario.waterUse ?? {}),
    ...(hasWorkloadRefinement || workload !== 'unknown' ? { workload } : {}),
    ...(hasOperatingModelRefinement || operatingModel !== 'unknown' ? { operatingModel } : {}),
    ...(ambientConditions ? { ambientConditions } : {}),
    ...refinedWaterUse,
  };
  // A declared IT boundary does not require a selected PUE to screen the site.
  // Preserve the recorded load, and qualify only its facility conversion with
  // the published PUE sensitivity (including when PUE is explicitly unknown).
  if (sourceBasis === 'it' && !waterContext.pue) {
    waterContext.pue = { ...WATER_USE_BENCHMARK_CATALOG.defaults.pue };
  }
  const validConversionPue = waterContext.pue
    && finitePositive(waterContext.pue.low)
    && finitePositive(waterContext.pue.high)
    && waterContext.pue.low >= 1
    && waterContext.pue.high >= waterContext.pue.low
      ? waterContext.pue
      : undefined;
  const sourceLowMw = loadMw ?? loadRange!.low;
  const sourceHighMw = loadMw ?? loadRange!.high;
  const convertsItLoad = sourceBasis === 'it' && validConversionPue != null;
  const scaleMw = (mw: number, factor: number): number => Math.round(mw * factor * 1_000_000) / 1_000_000;
  const jobsLowMw = convertsItLoad ? scaleMw(sourceLowMw, validConversionPue.low) : sourceLowMw;
  const jobsHighMw = convertsItLoad ? scaleMw(sourceHighMw, validConversionPue.high) : sourceHighMw;
  const jobsModelBasis: PowerMwBasis = sourceBasis === 'facility' || convertsItLoad
    ? 'utility-service'
    : sourceBasis;
  const basisTreatment = convertsItLoad
    ? 'it-pue-to-facility-as-utility-service-screening-proxy' as const
    : sourceBasis === 'facility' && basisSource === 'screening-assumption'
      ? 'unspecified-as-facility-as-utility-service-screening-proxy' as const
      : sourceBasis === 'facility'
        ? 'facility-as-utility-service-screening-proxy' as const
        : 'as-recorded' as const;
  const load = {
    value: jobsLowMw,
    basis: jobsModelBasis,
    phaseBasis,
    basisSource,
    ...(basisTreatment !== 'as-recorded' ? { basisTreatment } : {}),
    ...(sourceBasis !== jobsModelBasis ? { sourceBasis } : {}),
    ...(convertsItLoad ? { basisConversionPue: validConversionPue } : {}),
  } as const;
  const hasJobsRange = loadRange != null || jobsHighMw !== jobsLowMw;
  const waterLoadRange = {
    low: sourceLowMw,
    high: sourceHighMw,
    basis: sourceBasis,
    phaseBasis,
    basisSource,
    basisTreatment: 'as-recorded' as const,
  };
  const directJobs = {
    schemaVersion: ECONOMIC_CONTRIBUTION_SCHEMA_VERSION,
    siteId: site.id,
    location: { stateFips: '', countyFips: '' },
    load,
    operatingModel,
    workload,
    ...(refinements?.operatorPermanentOnsiteFte != null && refinements.operatorPermanentJobsSource
      ? {
          operatorProvided: {
            permanentOnsiteFte: refinements.operatorPermanentOnsiteFte,
            sourceLabel: refinements.operatorPermanentJobsSource,
          },
        }
      : {}),
  } satisfies EconomicContributionScreenRequest['directJobs'];
  const duration = finitePositive(refinements?.constructionDurationMonths)
    ? refinements.constructionDurationMonths
    : scenario.constructionDurationMonths;
  const additionalScenarios: EconomicContributionScenarioInput[] = [];
  if (options.includeScenarios !== false) {
    const cases = defaults.scenarioOptions.filter(option => !option.disabledReason);
    for (const candidate of cases) {
      const bases: PowerMwBasis[] = candidate.loadBasisSource === 'screening-assumption'
        ? ['facility', 'it'] : [candidate.loadBasis];
      for (const basis of bases) {
        if (candidate.phaseBasis === phaseBasis && basis === sourceBasis) continue;
        const variant = economicScreenRequestFromSite(site, {
          ...options,
          includeScenarios: false,
          scenario: { ...candidate, loadBasis: basis },
        });
        if (!variant) continue;
        additionalScenarios.push({
          ...variant,
          scenarioId: `${candidate.phaseBasis}:${basis}`,
          label: `${candidate.label}; ${basis === 'it' ? 'IT-load' : 'facility-demand'} case`,
          assumption: candidate.loadBasisSource === 'screening-assumption'
            ? `Conditional estimate: treats the declared cumulative MW as ${basis === 'it' ? 'IT load' : 'facility demand'}. The actual MW boundary remains unknown; this is not a net-generation, firm-supply, or confirmed demand value.`
            : 'Uses the recorded cumulative phase MW boundary; staffing and cooling quantities remain modeled screening estimates.',
        });
      }
    }
  }
  return {
    schemaVersion: ECONOMIC_CONTRIBUTION_SCHEMA_VERSION,
    ...(additionalScenarios.length ? { additionalScenarios } : {}),
    directJobs,
    ...(hasJobsRange ? {
      directJobsLoadRange: {
        low: jobsLowMw,
        high: jobsHighMw,
        basis: jobsModelBasis,
        phaseBasis,
        basisSource,
        ...(sourceBasis !== jobsModelBasis ? { sourceBasis } : {}),
        basisTreatment,
        ...(convertsItLoad ? { basisConversionPue: validConversionPue } : {}),
      },
    } : {}),
    constructionJobs: {
      schemaVersion: ECONOMIC_CONTRIBUTION_SCHEMA_VERSION,
      siteId: site.id,
      load,
      ...(duration ? { constructionDurationMonths: duration } : {}),
      operatorProvided: refinements?.operatorConstruction,
    },
    waterUse: {
      schemaVersion: ECONOMIC_CONTRIBUTION_SCHEMA_VERSION,
      siteId: site.id,
      siteCoordinates: {
        latitude: site.location.lat,
        longitude: site.location.lng,
      },
      loadRange: waterLoadRange,
      context: waterContext,
    },
    fiscalScenarios: refinements?.fiscalScenarios,
  };
}
